/**
 * Which connected rows itemize their accounts — web follows NATIVE here.
 *
 * Its own module with **no runtime imports** so a unit test can exercise it without dragging the data
 * layer (`lib/connectors.ts` pulls `api-client`, which reaches common-lib and cannot be imported from a
 * jest test — see orion-web/CLAUDE.md → Tests). Both functions are re-exported from `lib/connectors.ts`,
 * so call sites import them from there as before.
 */

/** The minimal shape the rule reads — `ConnectorStatus` satisfies it structurally. */
export type AccountListConnector = {
  connected?: boolean;
  provider?: string;
};

/** Whether a row can hold several accounts at once — the per-account list and "Add another" hang off this.
 *  MCP and Wix-service rows are singletons: they have no per-account list or add-another flow. */
export function supportsMultipleAccounts(connector: AccountListConnector): boolean {
  return connector.provider !== 'mcp' && connector.provider !== 'wix-service';
}

/** Whether a connected row itemizes its accounts.
 *
 *  **This is native's rule, adopted by web** so a user sees the same accounts for the same tools on every
 *  surface: EVERY connected row itemizes while the server flag is on, whatever the account count and
 *  whether or not the server could resolve a label. An account with no label falls back to
 *  "Account {number}" (`connectors.accountFallback`) — exactly what iOS/Android render from the same
 *  payload.
 *
 *  Web used to additionally require 2+ accounts, or one with a resolvable label. That is what made Google
 *  Docs — one connected account, no label — show "0 accounts" on the web while both phones listed it
 *  (reported 2026-08-12). The payload was never the difference: web's `/api/connectors/status` and native's
 *  `connectors` native-data action both call
 *  `composioConnectorService.getStatus(ms, { resolveLabels: true })` → `assembleConnectorStatuses(...)` and
 *  both report `multiAccountEnabled: true`. Only the client gate differed.
 *
 *  Mirrors iOS `ConnectorRow` (`connector.connected && multiAccount`) and Android `ConnectorRow`
 *  (`expanded = multiAccount`), plus the `mcp` guard those two get structurally by routing MCP through a
 *  separate row. **Native is the reference — if this rule changes, it changes there first.** */
export function showsAccountList(connector: AccountListConnector, multiAccountEnabled: boolean): boolean {
  return !!connector.connected && multiAccountEnabled && supportsMultipleAccounts(connector);
}
